import { useMemo } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';

export interface RegionIndex {
  // AI trigger region names for the current map (type MytaAITrigger).
  regionNames: string[];
  // regionName -> the spot ueIdentifiers it owns (region.spots[]).
  regionSpots: Record<string, string[]>;
  // spotId (lowercased) -> owning region name. Lets a spot resolve to its
  // region so a script created for the region also covers the spot.
  spotToRegion: Record<string, string>;
}

const EMPTY_INDEX: RegionIndex = {
  regionNames: [],
  regionSpots: {},
  spotToRegion: {},
};

/**
 * Reads the AI trigger regions — and, crucially, the `spots[]` array each
 * region owns — from the event's pixel-streaming client resources
 * (`clientParams.resources.maps[].regions`, the same source the admin uses),
 * falling back to the room's map metadata. This is the bridge between an AI
 * trigger ("Expo #1") and the spots that live inside it ("WallArt_Pic_01"), so
 * one script can be created for both at once.
 */
export const useRegionIndex = (mapName: string | undefined): RegionIndex => {
  const { currentRoom, pixelStreamingClient } = useMytaverseEvent();

  return useMemo<RegionIndex>(() => {
    type Region = { name?: unknown; type?: unknown; spots?: unknown };
    type ResourceMap = { name?: unknown; regions?: Region[] };

    const toRegionList = (regions: Region[] | undefined): Region[] =>
      Array.isArray(regions)
        ? regions.filter((r) => r?.type === 'MytaAITrigger')
        : [];

    const resourceMaps = (
      pixelStreamingClient as
        | { clientParams?: { resources?: { maps?: ResourceMap[] } } }
        | null
        | undefined
    )?.clientParams?.resources?.maps;

    let regions: Region[] = [];
    if (Array.isArray(resourceMaps) && resourceMaps.length > 0) {
      // The resource map name is a path like "/…/Maps/Map_SpiralMuseum"; prefer
      // the one matching the current map, otherwise flatten all maps.
      const scoped = mapName
        ? resourceMaps.filter(
            (m) => typeof m?.name === 'string' && m.name.includes(mapName),
          )
        : [];
      const source = scoped.length > 0 ? scoped : resourceMaps;
      regions = source.flatMap((m) => toRegionList(m?.regions));
    }

    if (regions.length === 0) {
      const roomRegions = (
        currentRoom?.map as { regions?: Region[] } | undefined
      )?.regions;
      regions = toRegionList(roomRegions);
    }

    if (regions.length === 0) {
      return EMPTY_INDEX;
    }

    const regionNames: string[] = [];
    const regionSpots: Record<string, string[]> = {};
    const spotToRegion: Record<string, string> = {};

    for (const region of regions) {
      const name = typeof region.name === 'string' ? region.name : '';
      if (!name) {
        continue;
      }
      if (!regionNames.includes(name)) {
        regionNames.push(name);
      }
      const spots = Array.isArray(region.spots)
        ? region.spots.filter((s): s is string => typeof s === 'string' && !!s)
        : [];
      regionSpots[name] = spots;
      for (const spot of spots) {
        spotToRegion[spot.toLowerCase()] = name;
      }
    }

    return { regionNames, regionSpots, spotToRegion };
  }, [pixelStreamingClient, mapName, currentRoom?.map]);
};
